<template>
  <b-container v-if="brand">
    <b-row align-h="start" class="mb-4">
      <b-overlay :show="loading" no-wrap fixed />
      <b-col>
        <h1>Brand: {{ brand.$.name }}</h1>
      </b-col>
      <b-col class="text-right">
        <saved-notice v-if="saved" />
        <b-button
          v-if="!readOnly"
          variant="primary"
          class="ml-2 mb-1"
          @click="onSave"
        >
          Save
        </b-button>
        <b-button
          variant="primary"
          class="ml-2 mb-1"
          @click="$router.push('/brands')"
        >
          Back
        </b-button>
      </b-col>
    </b-row>
    <error-alert :errors="errors" class="mb-3" />
    <b-row class="base-form mb-3">
      <b-col xl="6" sm="12">
        <b-form-group label="Name">
          <b-form-input v-model="brand.name" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Status">
          <b-form-select
            v-model="brand.status"
            :disabled="readOnly"
            :options="statusOptions"
          />
        </b-form-group>
        <b-form-group label="Description">
          <b-form-textarea
            v-model="brand.description"
            :disabled="readOnly"
            :rows="3"
          />
        </b-form-group>
      </b-col>
      <b-col xl="6" sm="12">
        <b-form-group label="Health Score">
          <b-form-select
            v-model="brand.health_score"
            :disabled="readOnly"
            :options="healthScoreOptions"
          />
        </b-form-group>
        <b-form-group label="Contact Phone">
          <b-form-input v-model="brand.phone" :disabled="readOnly" />
        </b-form-group>
        <b-form-group label="Hours">
          <b-form-input v-model="brand.hours" :disabled="readOnly" />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="base-form mb-3">
      <b-col lg="6" sm="12">
        <template v-if="brand.logo">
          <div>
            <span class="mr-2 col-form-label">Logo</span>
            <b-link v-if="!readOnly" class="mr-2" @click="onRemoveImage('logo')"
              >Remove</b-link
            >
          </div>
          <div>
            <img
              class="mt-3 mb-2"
              :src="awsImageUrl(brand.logo, 100, 100)"
              alt="brand logo"
            />
          </div>
        </template>
        <template v-else>
          <b-form-group label="Logo">
            <error-alert :errors="logoError" class="mb-2" />
            <b-form-file
              v-model="logoFile"
              :disabled="readOnly"
              accept="image/jpeg, image/png, image/jpg"
              :state="Boolean(logoFile)"
              placeholder="Choose an image file or drop it here...."
              drop-placeholder="Drop file here..."
            />
            <p class="helper-text">
              Must be under 2MB and .png, .jpg, or .jpeg
            </p>
            <b-button
              variant="primary"
              :disabled="readOnly"
              @click="onUploadImage('logo')"
              >Upload</b-button
            >
          </b-form-group>
        </template>
      </b-col>
      <b-col lg="6" sm="12">
        <template v-if="brand.icon">
          <div>
            <span class="mr-2 col-form-label">Icon</span>
            <b-link v-if="!readOnly" class="mr-2" @click="onRemoveImage('icon')"
              >Remove</b-link
            >
          </div>
          <div>
            <img
              class="mt-3 mb-2"
              :src="awsImageUrl(brand.icon, 50, 50)"
              alt="brand icon"
            />
          </div>
        </template>
        <template v-else>
          <b-form-group label="Icon">
            <error-alert :errors="iconError" class="mb-2" />
            <b-form-file
              v-model="iconFile"
              :disabled="readOnly"
              accept="image/jpeg, image/png, image/jpg"
              :state="Boolean(iconFile)"
              placeholder="Choose an image file or drop it here...."
              drop-placeholder="Drop file here..."
            />
            <p class="helper-text">
              Must be under 2MB and .png, .jpg, or .jpeg
            </p>
            <b-button
              variant="primary"
              :disabled="readOnly"
              @click="onUploadImage('icon')"
              >Upload</b-button
            >
          </b-form-group>
        </template>
      </b-col>
    </b-row>
    <b-row class="overflow-auto" no-gutters>
      <table style="min-width: 1140px">
        <thead>
          <tr>
            <th class="text-center">
              <span class="icon">
                <img
                  id="hours-tooltip"
                  :src="asset('/imgs/icons/info.svg')"
                  alt="hours info"
                  class="mb-1"
              /></span>
            </th>
            <th>Kitchen Hours</th>
            <th>Hub</th>
            <th>Status</th>
            <th>Open</th>
            <th>Close</th>
            <th class="text-center">All</th>
            <th class="text-center">Mon</th>
            <th class="text-center">Tue</th>
            <th class="text-center">Wed</th>
            <th class="text-center">Thu</th>
            <th class="text-center">Fri</th>
            <th class="text-center">Sat</th>
            <th class="text-center">Sun</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <template v-for="(kitchen, kIndex) in kitchens">
            <template v-if="kitchen.availabilities.length">
              <tr
                v-for="(avail, aIndex) in kitchen.availabilities"
                :key="kIndex + '-' + aIndex"
                class="text-center"
              >
                <td
                  v-if="aIndex === 0"
                  :rowspan="kitchen.availabilities.length"
                  class="text-left"
                >
                  <b-button
                    v-if="!readOnly"
                    variant="link"
                    class="text-primary"
                    @click="addAvailability(kIndex)"
                  >
                    <span class="icon">
                      <img
                        :src="asset('/imgs/icons/add.svg')"
                        alt="add"
                        title="Add kitchen hours" /></span
                  ></b-button>
                </td>
                <td
                  v-if="aIndex === 0"
                  :rowspan="kitchen.availabilities.length"
                  class="text-left"
                >
                  {{ kitchen.name }}
                </td>
                <td
                  v-if="aIndex === 0"
                  :rowspan="kitchen.availabilities.length"
                  class="text-left"
                >
                  {{ kitchen.facility.name }}
                </td>
                <td>
                  <b-form-select
                    v-model="avail.status"
                    :options="availabilityStatusOptions"
                    :disabled="readOnly"
                    @change="toggleStatus(kIndex, aIndex, $event)"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model="avail.open"
                    :disabled="avail.status !== 'open' || readOnly"
                    type="time"
                  />
                </td>
                <td>
                  <b-form-input
                    v-model="avail.close"
                    :disabled="avail.status !== 'open' || readOnly"
                    type="time"
                  />
                </td>
                <td>
                  <b-form-checkbox
                    v-model="avail.all"
                    :value="1"
                    :unchecked-value="0"
                    :disabled="readOnly"
                    @change="toggleAllTimes(kIndex, aIndex, $event)"
                  />
                </td>
                <td v-for="day in days" :key="day">
                  <b-form-checkbox
                    v-model="avail[day]"
                    :value="1"
                    :unchecked-value="0"
                    :disabled="readOnly"
                    @change="toggleSingleDay(kIndex, aIndex, day, $event)"
                  />
                </td>
                <td class="text-center">
                  <b-button
                    v-if="!readOnly"
                    variant="white"
                    class="text-primary"
                    :disabled="readOnly"
                    @click="removeAvailability(kIndex, aIndex)"
                  >
                    <b-icon icon="X" />
                  </b-button>
                </td>
              </tr>
            </template>
            <template v-else>
              <tr :key="kIndex" class="text-center">
                <td>
                  <b-button
                    v-if="!readOnly"
                    variant="link"
                    class="text-primary"
                    @click="addAvailability(kIndex)"
                  >
                    <span class="icon">
                      <img
                        :src="asset('/imgs/icons/add.svg')"
                        alt="add"
                        title="Add kitchen hours" /></span
                  ></b-button>
                </td>
                <td class="text-left">{{ kitchen.name }}</td>
                <td class="text-left">{{ kitchen.facility.name }}</td>
                <td class="text-left">Open</td>
              </tr>
            </template>
          </template>
        </tbody>
      </table>
    </b-row>
    <b-tooltip target="hours-tooltip" triggers="hover" boundary="window">
      If this brand has limited hours (separate from overall hub operations)
      please indicate those here. If the brand does not operate for some days,
      click the red plus sign to designate a closed day. Otherwise, default
      values are the hours of the hub.
    </b-tooltip>
  </b-container>
</template>

<script>
import { mapGetters } from 'vuex';
import Brand from '../../models/Brand.js';
import ErrorAlert from '../../components/ErrorAlert';
import ResourceAPI from '../../services/ResourceAPI';
import SavedNotice from '../../components/SavedNotice';

export default {
  name: 'Brand',
  components: {
    ErrorAlert,
    SavedNotice,
  },
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      saved: false,
      errors: {},
      brand: null,
      logoFile: null,
      logoError: {},
      iconFile: null,
      iconError: {},
      kitchens: [],
      healthScoreOptions: [
        {
          text: 'A',
          value: 'A',
        },
        {
          text: 'B',
          value: 'B',
        },
        {
          text: 'C',
          value: 'C',
        },
        {
          text: 'Unknown',
          value: 'Unknown',
        },
      ],
      statusOptions: [
        {
          value: 'active',
          text: 'Active',
        },
        {
          value: 'inactive',
          text: 'Inactive',
        },
        {
          value: 'archived',
          text: 'Archived',
        },
      ],
      availabilityStatusOptions: [
        {
          text: 'Open',
          value: 'open',
        },
        {
          text: 'Closed',
          value: 'closed',
        },
      ],
      days: [
        'monday',
        'tuesday',
        'wednesday',
        'thursday',
        'friday',
        'saturday',
        'sunday',
      ],
    };
  },
  computed: {
    ...mapGetters('session', ['brandIds']),
    readOnly() {
      return !this.brandIds.includes(this.id);
    },
  },
  mounted() {
    this.refreshRecord();
  },
  methods: {
    refreshRecord() {
      const me = this;

      me.loading = true;
      const brandRaw = new Brand({
        id: me.id,
      });
      brandRaw
        .fetch()
        .then(() => {
          me.loadBrandAvailabilities();
          me.brand = brandRaw;
        })
        .catch((error) => {
          alert(error);
        });
    },

    // Load brand availability records to populate hours table
    loadBrandAvailabilities() {
      const me = this;

      ResourceAPI.getRecords('brand-availabilities', {
        brand: me.id,
        limit: 100,
      })
        .then((response) => {
          me.buildKitchenData(response.data);
          me.loading = false;
        })
        .catch((error) => {
          alert(error);
        });
    },

    // Construct hour of operation data around brand kitchens
    buildKitchenData(availabilities) {
      const kitchens = [];

      // Match brand fulfillment centers with their respective availability rules
      for (let i = 0; i < this.brand.fulfillment_centers.length; i += 1) {
        const brandKitchen = this.brand.fulfillment_centers[i];
        brandKitchen.availabilities = availabilities.filter(
          (el) => el.fulfillment_center_id === brandKitchen.id
        );

        for (let a = 0; a < brandKitchen.availabilities.length; a += 1) {
          brandKitchen.availabilities[a].all = this.allAvailabilitiesSelected(
            brandKitchen.availabilities[a]
          );
        }

        kitchens.push(brandKitchen);
      }

      this.kitchens = kitchens;
    },

    // Determine if all days in a brand availability are true
    allAvailabilitiesSelected(availabilty) {
      for (let i = 0; i < this.days.length; i += 1) {
        if (!availabilty[this.days[i]]) {
          return 0;
        }
      }

      return 1;
    },

    // Toggle availability status value
    toggleStatus(kitchenIndex, availabilityIndex, value) {
      const kitchen = this.kitchens[kitchenIndex];

      kitchen.availabilities[availabilityIndex].status = value;
      if (value === 'closed') {
        kitchen.availabilities[availabilityIndex].open = null;
        kitchen.availabilities[availabilityIndex].close = null;
      }

      this.$set(this.kitchens, kitchenIndex, kitchen);
    },

    // Toggle all availabilities to true / false
    toggleAllTimes(kitchenIndex, availabilityIndex, value) {
      const kitchen = this.kitchens[kitchenIndex];

      for (let i = 0; i < this.days.length; i += 1) {
        kitchen.availabilities[availabilityIndex][this.days[i]] = value;
      }

      this.$set(this.kitchens, kitchenIndex, kitchen);
    },

    // Update availability all value on single day change
    toggleSingleDay(kitchenIndex, availabilityIndex, day, value) {
      const kitchen = this.kitchens[kitchenIndex];

      kitchen.availabilities[availabilityIndex][day] = value;
      kitchen.availabilities[availabilityIndex].all =
        this.allAvailabilitiesSelected(
          kitchen.availabilities[availabilityIndex]
        );
      this.$set(this.kitchens, kitchenIndex, kitchen);
    },

    // Add new availability option
    addAvailability(kitchenIndex) {
      const kitchen = this.kitchens[kitchenIndex];
      const avail = {
        all: 0,
        status: 'open',
        open: null,
        close: null,
        brand_id: this.id,
        fulfillment_center_id: kitchen.id,
      };

      for (let i = 0; i < this.days.length; i += 1) {
        avail[this.days[i]] = 0;
      }

      kitchen.availabilities.push(avail);
      this.$set(this.kitchens, kitchenIndex, kitchen);
    },

    // Remove availability option
    removeAvailability(kitchenIndex, availabilityIndex) {
      const kitchen = this.kitchens[kitchenIndex];

      kitchen.availabilities.splice(availabilityIndex, 1);
      this.$set(this.kitchens, kitchenIndex, kitchen);
    },

    // Build brand availability object to POST to server
    buildAvailabilities() {
      const avails = [];

      for (let i = 0; i < this.kitchens.length; i += 1) {
        for (let a = 0; a < this.kitchens[i].availabilities.length; a += 1) {
          const kAvail = this.kitchens[i].availabilities[a];
          const availData = {
            brand_id: this.id,
            fulfillment_center_id: kAvail.fulfillment_center_id,
            status: kAvail.status,
            open: kAvail.status === 'open' ? kAvail.open : null,
            close: kAvail.status === 'open' ? kAvail.close : null,
          };

          for (let b = 0; b < this.days.length; b += 1) {
            availData[this.days[b]] = kAvail[this.days[b]];
          }

          avails.push(availData);
        }
      }

      return avails;
    },

    // Save record
    onSave() {
      const me = this;
      const availabilities = me.buildAvailabilities();

      for (let i = 0; i < availabilities.length; i += 1) {
        if (
          availabilities[i].status === 'open' &&
          (!availabilities[i].open || !availabilities[i].close)
        ) {
          me.errors = {
            availability: [
              "All 'Open' kitchen hours must have a valid open and close time.",
            ],
          };
          return;
        }
      }

      me.loading = true;
      me.brand.set('brandAvailability', availabilities);

      me.brand
        .save()
        .then((response) => {
          me.loading = false;
          const data = response.getData();

          if (data.success) {
            me.saved = true;
            setTimeout(() => {
              me.saved = false;
            }, 5000);
            me.errors = {};
            me.refreshRecord();
          }
        })
        .catch((error) => {
          me.saved = false;
          me.loading = false;
          me.errors = error.response.data.errors;
        });
    },

    // Handle uploading image file
    onUploadImage(field) {
      const me = this;

      if (!me[`${field}File`]) {
        me[`${field}Error`] = true;
        return;
      }

      me.loading = true;

      ResourceAPI.uploadFile('upload-image', {
        file: me[`${field}File`],
        title: me[`${field}File`].name,
      })
        .then((response) => {
          if (response.success) {
            me[`${field}Error`] = {};
            me[`${field}File`] = null;
            me.brand.set(field, response.data);
            me.brand.set(`${field}_id`, response.data.id);
          } else {
            me[`${field}Error`] = {
              file:
                response.status === 403
                  ? "Sorry, you don't have permission to upload files."
                  : "There was an issue uploading your image, please it's verify under 2mb and a jpg, jpeg or .png.",
            };
          }
          me.loading = false;
        })
        .catch((error) => alert(error));
    },

    // Remove image from UI
    onRemoveImage(field) {
      this.brand.set(field, null);
      this.brand.set(`${field}_id`, null);
    },
  },
};
</script>
